import { BehaviorSubject, Observable } from 'rxjs';
import {
  REMOTE_SUPPORT_PERMISSIONS,
  RemoteSupportClientFrame,
  RemoteSupportGrant,
  RemoteSupportServerFrame,
} from 'app/shared/models/remote-support.model';
import { loadNoVnc, NoVncRfb, NoVncRfbConstructor } from './novnc-loader';
import { ScreenChannel } from './screen-channel';
import {
  DEFAULT_SCREEN_PRESET,
  SCREEN_SETTINGS,
  ScreenPreset,
  ScreenQuality,
} from './screen-quality';

/** Where the session is: signalling first, then the peer connection to the terminal. */
export type RemoteSupportPhase =
  | 'signalling'
  | 'waiting-terminal'
  | 'connecting'
  | 'connected'
  | 'ended';

export type RemoteSupportScreenState =
  | 'off'
  | 'connecting'
  | 'showing'
  | 'unavailable'
  | 'not-permitted'
  | 'no-permission';

/** Whether the peer connection runs straight to the terminal or through a TURN relay. */
export type RemoteSupportPath = 'direct' | 'relay' | 'unknown';

/** Why the page itself gave the session up, as opposed to isoFarm or the terminal ending it. */
export type RemoteSupportFailure =
  | 'ticket-refused'
  | 'signalling-lost'
  | 'no-path';

export interface RemoteSupportSnapshot {
  phase: RemoteSupportPhase;
  screen: RemoteSupportScreenState;
  path: RemoteSupportPath;
  permissions: string[];
  /**
   * Whether the terminal's app draws its screen turned 90° counter-clockwise, so the page shows it
   * turned back (`screen-orientation`). False until the terminal says otherwise, and for terminals
   * whose software never says.
   */
  rotated: boolean;
  /** isoFarm's or the terminal's reason, when one of them ended the session. */
  endReason: string | null;
  failure: RemoteSupportFailure | null;
}

/** Browser APIs the session runs on; tests replace them. */
export interface RemoteSupportSessionDeps {
  openSocket(url: string): WebSocket;
  createPeerConnection(configuration: RTCConfiguration): RTCPeerConnection;
  loadRfb(): Promise<NoVncRfbConstructor>;
}

const BROWSER_DEPS: RemoteSupportSessionDeps = {
  openSocket: (url) => new WebSocket(url),
  createPeerConnection: (configuration) => new RTCPeerConnection(configuration),
  loadRfb: loadNoVnc,
};

const SOCKET_OPEN = 1;
/** Close status of a technician channel whose hello came late or carried a refused ticket. */
const POLICY_VIOLATION = 1008;
/** How long one peer connection may take to come up. */
const CONNECT_TIMEOUT_MS = 30_000;
/** Peer connections in a row that may fail, once one was up, before the session is given up. */
const MAX_FAILED_LINKS = 3;
/** The contract has the web page ask for the screen again every 5 s while it is unavailable. */
const SCREEN_RETRY_MS = 5_000;
/** After `not-permitted`, no `vnc` channel for a minute unless the permissions change first. */
const REFUSAL_HOLD_MS = 60_000;
/** How long `end` waits for isoFarm's echo before the page closes on its own. */
const END_REPLY_TIMEOUT_MS = 3_000;
const TECHNICIAN_ENDED = 'technician-ended';

const CONTROL_CHANNEL = 'control';
const VNC_CHANNEL = 'vnc';

/** The fields of WebRTC statistics the connection path is read from. */
interface StatsEntry {
  id: string;
  type: string;
  selectedCandidatePairId?: string;
  selected?: boolean;
  localCandidateId?: string;
  candidateType?: string;
}

interface PeerLink {
  pc: RTCPeerConnection;
  connected: boolean;
  remoteReady: boolean;
  pendingCandidates: RTCIceCandidateInit[];
  timer: ReturnType<typeof setTimeout> | null;
}

/**
 * One technician client of a remote support session (agronave-app `remotesupport/PROTOCOL.md`):
 * the signalling channel to isoFarm, one WebRTC peer connection to the terminal, and the
 * terminal's screen in noVNC over a `vnc` data channel. The page offers only what the account's
 * permissions allow; the terminal enforces them either way.
 */
export class RemoteSupportSession {
  private readonly state: BehaviorSubject<RemoteSupportSnapshot>;
  readonly state$: Observable<RemoteSupportSnapshot>;

  private socket: WebSocket | null = null;
  private link: PeerLink | null = null;
  private everConnected = false;
  private failedLinks = 0;
  private screenTarget: HTMLElement | null = null;
  private rfb: NoVncRfb | null = null;
  private screenPreset: ScreenPreset = DEFAULT_SCREEN_PRESET;
  private quality: ScreenQuality | null = null;
  private screenPending = false;
  private screenTimer: ReturnType<typeof setTimeout> | null = null;
  private refusedAt: number | null = null;
  private endTimer: ReturnType<typeof setTimeout> | null = null;
  /** The technician asked to end; the `end` frame goes out once the channel is open. */
  private endRequested = false;
  private finished = false;

  constructor(
    private readonly grant: RemoteSupportGrant,
    private readonly deps: RemoteSupportSessionDeps = BROWSER_DEPS,
  ) {
    this.state = new BehaviorSubject<RemoteSupportSnapshot>({
      phase: 'signalling',
      screen: 'off',
      path: 'unknown',
      permissions: grant.permissions,
      rotated: false,
      endReason: null,
      failure: null,
    });
    this.state$ = this.state.asObservable();
  }

  get snapshot(): RemoteSupportSnapshot {
    return this.state.value;
  }

  /** Opens the technician channel; the ticket goes in the first frame, never in the URL. */
  start(): void {
    const socket = this.deps.openSocket(this.grant.signalingUrl);
    this.socket = socket;
    socket.onopen = () => {
      this.send({ type: 'hello', ticket: this.grant.ticket });
      if (this.endRequested) {
        this.sendEnd();
      } else {
        this.update({ phase: 'waiting-terminal' });
      }
    };
    socket.onmessage = (event) => this.receive(event.data);
    socket.onclose = (event) => this.onSocketClosed(event);
  }

  /** The element noVNC draws in. The screen opens once the peer connection is up. */
  attachScreen(target: HTMLElement | null): void {
    this.screenTarget = target;
    if (!target) {
      this.closeScreen();
    } else if (!this.rfb) {
      this.scheduleScreen(0);
    }
  }

  /**
   * What the page asks the terminal's screen for: at once on the open screen, and on every screen
   * the session opens after.
   */
  setScreenPreset(preset: ScreenPreset): void {
    this.screenPreset = preset;
    this.quality?.apply(SCREEN_SETTINGS[preset]);
  }

  /**
   * Ends the session for the terminal and for every client of it (`technician-ended`).
   *
   * Asked before the technician channel is open, the `end` goes out right after the hello: isoFarm
   * only takes it from a client whose ticket it accepted. The page reports the session ended only
   * once the frame was sent; a channel that never opens ends as `signalling-lost` instead.
   */
  end(): void {
    if (this.finished || this.endRequested) {
      return;
    }
    this.endRequested = true;
    if (this.socket?.readyState === SOCKET_OPEN) {
      this.sendEnd();
    }
  }

  /**
   * Leaves without ending the session: the terminal hears `technician-left`, and the account that
   * owns the session may connect again with the same code while it lasts.
   */
  leave(): void {
    this.finish({});
  }

  private receive(data: unknown): void {
    const frame = parseFrame(data);
    if (!frame) {
      return;
    }
    switch (frame.type) {
      case 'joined':
        this.onJoined();
        break;
      case 'answer':
        this.onAnswer(frame.sdp);
        break;
      case 'candidate':
        this.onCandidate(frame.candidate, frame.mid);
        break;
      case 'permissions':
        this.onPermissions(frame.permissions);
        break;
      case 'end':
        this.finish({ endReason: frame.reason });
        break;
      default:
        // host-key matters only to SSH, which the browser does not offer.
        break;
    }
  }

  private onJoined(): void {
    if (!this.link) {
      this.startLink();
    }
  }

  /** Replaces the current peer connection, if any, with a new one and offers it. */
  private startLink(): void {
    this.closeLink();
    const pc = this.deps.createPeerConnection({
      iceServers: this.grant.iceServers,
    });
    const link: PeerLink = {
      pc,
      connected: false,
      remoteReady: false,
      pendingCandidates: [],
      timer: null,
    };
    this.link = link;
    this.update({ phase: 'connecting', screen: 'off', path: 'unknown' });

    pc.onicecandidate = (event) => {
      if (link === this.link && event.candidate?.candidate) {
        this.send({
          type: 'candidate',
          candidate: event.candidate.candidate,
          mid: event.candidate.sdpMid ?? '',
        });
      }
    };
    pc.onconnectionstatechange = () => this.onLinkState(link);
    // The control channel puts the SCTP association in the offer, so a vnc channel can open
    // later without renegotiation.
    pc.createDataChannel(CONTROL_CHANNEL);
    link.timer = setTimeout(() => {
      if (!link.connected) {
        this.onLinkFailed(link);
      }
    }, CONNECT_TIMEOUT_MS);

    pc.createOffer()
      .then((offer) => pc.setLocalDescription(offer).then(() => offer))
      .then((offer) => {
        if (link === this.link) {
          this.send({ type: 'offer', sdp: offer.sdp ?? '' });
        }
      })
      .catch(() => this.onLinkFailed(link));
  }

  private onLinkState(link: PeerLink): void {
    if (link !== this.link) {
      return;
    }
    const state = link.pc.connectionState;
    if (state === 'connected' && !link.connected) {
      link.connected = true;
      this.clearLinkTimer(link);
      this.everConnected = true;
      this.failedLinks = 0;
      this.update({ phase: 'connected' });
      this.detectPath(link);
      this.openScreen();
    } else if (state === 'failed' || state === 'closed') {
      this.onLinkFailed(link);
    }
  }

  /**
   * A link that never came up means there is no path to the terminal, direct or through the
   * session's TURN relay: trying again finds the same networks. A link that was up is replaced,
   * which the terminal accepts for 30 s after it lost the old one.
   */
  private onLinkFailed(link: PeerLink): void {
    if (link !== this.link || this.finished) {
      return;
    }
    this.failedLinks += 1;
    if (!this.everConnected || this.failedLinks >= MAX_FAILED_LINKS) {
      this.finish({ failure: 'no-path' });
    } else {
      this.startLink();
    }
  }

  private onAnswer(sdp: string): void {
    const link = this.link;
    if (!link) {
      return;
    }
    link.pc
      .setRemoteDescription({ type: 'answer', sdp })
      .then(() => {
        link.remoteReady = true;
        link.pendingCandidates
          .splice(0)
          .forEach((candidate) => this.addCandidate(link, candidate));
      })
      .catch(() => {
        // An answer to the offer of a link already replaced: the current link times out without
        // one and is replaced in turn.
        if (!this.everConnected) {
          this.onLinkFailed(link);
        }
      });
  }

  private onCandidate(candidate: string, mid: string): void {
    const link = this.link;
    if (!link || !candidate) {
      return;
    }
    const init: RTCIceCandidateInit = { candidate };
    if (mid) {
      init.sdpMid = mid;
    } else {
      init.sdpMLineIndex = 0;
    }
    if (link.remoteReady) {
      this.addCandidate(link, init);
    } else {
      link.pendingCandidates.push(init);
    }
  }

  private addCandidate(link: PeerLink, candidate: RTCIceCandidateInit): void {
    link.pc.addIceCandidate(candidate).catch(() => undefined);
  }

  /** isoFarm changed what the account may do while the client is in the session. */
  private onPermissions(permissions: string[]): void {
    this.refusedAt = null;
    this.update({ permissions });
    if (!this.permits(REMOTE_SUPPORT_PERMISSIONS.SCREEN)) {
      this.closeScreen();
      this.update({ screen: 'no-permission' });
    } else if (this.rfb) {
      this.rfb.viewOnly = !this.permits(
        REMOTE_SUPPORT_PERMISSIONS.SCREEN_INPUT,
      );
    } else {
      this.openScreen();
    }
  }

  private detectPath(link: PeerLink): void {
    link.pc
      .getStats()
      .then((report) => {
        if (link === this.link) {
          this.update({ path: connectionPathOf(report) });
        }
      })
      .catch(() => undefined);
  }

  /** Opens a `vnc` channel and noVNC on it, when the link, the target and the permissions allow. */
  private openScreen(): void {
    this.clearScreenTimer();
    const link = this.link;
    if (
      !link?.connected ||
      this.finished ||
      this.rfb ||
      this.screenPending ||
      !this.screenTarget
    ) {
      return;
    }
    if (!this.permits(REMOTE_SUPPORT_PERMISSIONS.SCREEN)) {
      this.update({ screen: 'no-permission' });
      return;
    }
    const held = this.refusalHoldLeft();
    if (held > 0) {
      this.update({ screen: 'not-permitted' });
      this.scheduleScreen(held);
      return;
    }
    this.update({ screen: 'connecting' });
    this.screenPending = true;
    // noVNC is attached before the channel can open, so no RFB message arrives unheard.
    this.deps
      .loadRfb()
      .then((Rfb) => this.showScreen(Rfb, link))
      .catch(() => this.retryScreen());
  }

  private showScreen(Rfb: NoVncRfbConstructor, link: PeerLink): void {
    this.screenPending = false;
    if (
      link !== this.link ||
      !link.connected ||
      this.finished ||
      !this.screenTarget
    ) {
      return;
    }
    const channel = new ScreenChannel(link.pc.createDataChannel(VNC_CHANNEL), {
      screenUnavailable: () => this.update({ screen: 'unavailable' }),
      notPermitted: () => {
        this.refusedAt = Date.now();
        this.update({ screen: 'not-permitted' });
      },
      screenOrientation: (rotated) => this.onScreenOrientation(rotated),
    });
    let rfb: NoVncRfb;
    try {
      rfb = new Rfb(this.screenTarget, channel, { shared: true });
    } catch (error) {
      channel.close();
      throw error;
    }
    // A new channel says its orientation again before its first RFB byte; until then, the last one.
    rfb.scaleViewport = !this.snapshot.rotated;
    rfb.focusOnClick = true;
    rfb.viewOnly = !this.permits(REMOTE_SUPPORT_PERMISSIONS.SCREEN_INPUT);
    rfb.addEventListener('connect', () => this.update({ screen: 'showing' }));
    rfb.addEventListener('disconnect', () => this.onScreenClosed(rfb));
    this.rfb = rfb;
    this.quality = new ScreenQuality(
      rfb,
      Rfb.messages,
      SCREEN_SETTINGS[this.screenPreset],
    );
  }

  /**
   * The page turns a rotated screen itself (ScreenRotation), which needs noVNC to draw at the
   * framebuffer's own size instead of fitting it to the viewer. The snapshot changes first, so the
   * page lays the screen out before noVNC rescales against it.
   */
  private onScreenOrientation(rotated: boolean): void {
    this.update({ rotated });
    if (this.rfb) {
      this.rfb.scaleViewport = !rotated;
    }
  }

  /**
   * The screen server restarts with the app while the peer connection stays up, so the page asks
   * again every 5 s, or after the refusal hold.
   */
  private onScreenClosed(rfb: NoVncRfb): void {
    if (rfb !== this.rfb) {
      return;
    }
    this.rfb = null;
    this.dropQuality();
    if (this.finished || !this.link?.connected) {
      return;
    }
    const held = this.refusalHoldLeft();
    if (held > 0) {
      this.update({ screen: 'not-permitted' });
      this.scheduleScreen(held);
    } else {
      this.retryScreen();
    }
  }

  private retryScreen(): void {
    this.screenPending = false;
    this.update({ screen: 'unavailable' });
    this.scheduleScreen(SCREEN_RETRY_MS);
  }

  private refusalHoldLeft(): number {
    if (this.refusedAt === null) {
      return 0;
    }
    return Math.max(0, this.refusedAt + REFUSAL_HOLD_MS - Date.now());
  }

  private scheduleScreen(delayMs: number): void {
    this.clearScreenTimer();
    this.screenTimer = setTimeout(() => {
      this.screenTimer = null;
      this.openScreen();
    }, delayMs);
  }

  private clearScreenTimer(): void {
    if (this.screenTimer) {
      clearTimeout(this.screenTimer);
      this.screenTimer = null;
    }
  }

  private closeScreen(): void {
    this.clearScreenTimer();
    const rfb = this.rfb;
    this.rfb = null;
    this.dropQuality();
    if (rfb) {
      rfb.disconnect();
    }
  }

  private dropQuality(): void {
    this.quality?.dispose();
    this.quality = null;
  }

  private closeLink(): void {
    this.closeScreen();
    const link = this.link;
    this.link = null;
    if (link) {
      this.clearLinkTimer(link);
      link.pc.onicecandidate = null;
      link.pc.onconnectionstatechange = null;
      link.pc.close();
    }
  }

  private clearLinkTimer(link: PeerLink): void {
    if (link.timer) {
      clearTimeout(link.timer);
      link.timer = null;
    }
  }

  private onSocketClosed(event: CloseEvent): void {
    this.socket = null;
    if (event.code === POLICY_VIOLATION) {
      this.finish({ failure: 'ticket-refused' });
    } else {
      this.finish({ failure: 'signalling-lost' });
    }
  }

  private finish(outcome: {
    endReason?: string;
    failure?: RemoteSupportFailure;
  }): void {
    if (this.finished) {
      return;
    }
    this.finished = true;
    if (this.endTimer) {
      clearTimeout(this.endTimer);
      this.endTimer = null;
    }
    this.closeLink();
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onclose = null;
      socket.onmessage = null;
      socket.close();
    }
    this.update({
      phase: 'ended',
      screen: 'off',
      endReason: outcome.endReason ?? null,
      failure: outcome.failure ?? null,
    });
  }

  private sendEnd(): void {
    this.send({ type: 'end' });
    this.endTimer = setTimeout(
      () => this.finish({ endReason: TECHNICIAN_ENDED }),
      END_REPLY_TIMEOUT_MS,
    );
  }

  private send(frame: RemoteSupportClientFrame): void {
    if (this.socket?.readyState === SOCKET_OPEN) {
      this.socket.send(JSON.stringify(frame));
    }
  }

  private permits(permission: string): boolean {
    return this.snapshot.permissions.includes(permission);
  }

  private update(change: Partial<RemoteSupportSnapshot>): void {
    this.state.next({ ...this.state.value, ...change });
  }
}

/** A text frame of the technician channel, or null for anything that is not one. */
function parseFrame(data: unknown): RemoteSupportServerFrame | null {
  if (typeof data !== 'string') {
    return null;
  }
  try {
    const frame = JSON.parse(data);
    if (typeof frame?.type === 'string') {
      return frame as RemoteSupportServerFrame;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Reads from WebRTC statistics whether the selected candidate pair runs through a relay. Chrome
 * names the pair on the transport; Firefox flags it as selected.
 */
export function connectionPathOf(report: RTCStatsReport): RemoteSupportPath {
  const stats: StatsEntry[] = [];
  report.forEach((stat: StatsEntry) => stats.push(stat));
  const transport = stats.find(
    (stat) => stat.type === 'transport' && stat.selectedCandidatePairId,
  );
  let pair: StatsEntry | undefined = stats.find(
    (stat) => stat.type === 'candidate-pair' && stat.selected,
  );
  if (transport) {
    pair = stats.find((stat) => stat.id === transport.selectedCandidatePairId);
  }
  const local = stats.find((stat) => pair && stat.id === pair.localCandidateId);
  if (!local) {
    return 'unknown';
  }
  if (local.candidateType === 'relay') {
    return 'relay';
  }
  return 'direct';
}
